import { useState } from "react";
import { Card } from "../../../components/Card";
import { ImageView } from "../../../components/Image";
import { LottieLoading } from "../../../components/Loading/LottieLoading";
import { TooltipView } from "../../../components/Tooltip";
import { getInitial } from "../../../services/utils";

const colors = {
  "up": {
    text: "text-[#0B8042]",
    bg: "bg-[#E6F7E8]",
  },
  "equal": {
    text: "text-[#9D6F2B]",
    bg: "bg-[#FFF9C5]",
  },
  "down": {
    text: "text-[#9D2B2B]",
    bg: "bg-[#FFDDDD]",
  },
}

const DEFAULT_DRILLDOWN_TOOLTIP = "Click a team member to view their performance metrics and insights.";

const MODE_LABELS: Record<string, string> = {
  scores: "Praxis Score",
  sessions: "Sessions",
};

export function LeaderBoard({
  loading,
  data = [
    {
      profileImg: undefined,
      firstName: "Amit",
      lastName: "Kumar",
      praxisScore: 100,
    },
    {
      profileImg: undefined,
      firstName: "Rohan",
      lastName: "Kumar",
      praxisScore: 100,
    },
  ],
  title = "Leaderboard",
  onSelect,
  selectedUserID,
  allowDrilldown = false,
  drilldownTooltip,
  mode = "scores",
  availableModes = ["scores"],
  onModeChange,
  showLabel = false,
}: {
  loading?: boolean;
  data?: any;
  title?: string;
  onSelect?: (userID: string) => void;
  selectedUserID?: string;
  allowDrilldown?: boolean;
  /** Tooltip when drilldown is enabled. Defaults to DEFAULT_DRILLDOWN_TOOLTIP if not provided or empty. */
  drilldownTooltip?: string;
  mode?: string;
  availableModes?: string[];
  onModeChange?: (mode: string) => void;
  /** Render the backend-formatted `label` instead of the raw score / session count. */
  showLabel?: boolean;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const tooltipContent = (drilldownTooltip?.trim()) ? drilldownTooltip : DEFAULT_DRILLDOWN_TOOLTIP;
  const showModeSelector = availableModes.length > 1;
  const modeLabel = MODE_LABELS[mode] ?? mode;
  return (
    <Card className="lg:min-w-[430px] !px-3 h-full">
      <div className="flex items-center justify-between mb-4 px-3">
        <div className="flex items-center gap-2">
          <span className="text-xl text-text-primary">{title}</span>
          {allowDrilldown && (
            <TooltipView
              id="leaderboard-drilldown"
              content={tooltipContent}
            />
          )}
        </div>
        {showModeSelector && (
          <div
            className="relative"
            tabIndex={-1}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setDropdownOpen(false);
              }
            }}
          >
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              className="flex items-center gap-1 text-xs font-medium text-[#3F3F46] uppercase tracking-wide"
            >
              {modeLabel}
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="mt-px">
                <path d="M1 1L5 5L9 1" stroke="#3F3F46" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-[#E4E4E7] rounded-md shadow-md z-10 min-w-[130px]">
                {availableModes.map((m) => (
                  <button
                    key={m}
                    onClick={() => { onModeChange?.(m); setDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs uppercase tracking-wide hover:bg-[#F5F5F5] ${m === mode ? "font-semibold text-text-primary" : "text-[#3F3F46]"}`}
                  >
                    {MODE_LABELS[m] ?? m}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {!loading && data.length === 0 && (
        <div className="flex justify-center items-center h-full">
          <div className="text-text-primary text-sm">No data found</div>
        </div>
      )}
      {loading ? <LottieLoading className="!w-full !h-[100px]" /> : (
        <div>
          {data.length > 0 && <div className="flex mb-4 px-2 uppercase items-center justify-between text-sm text-headerPrimary font-semibold">
            <div>Name</div>
            <div>{modeLabel}</div>
          </div>}
          <>
            {data
              .sort((a, b) => a.rank - b.rank)
              .map((item, index) => {
                const statusData = colors[Number(item?.score) < 3 ? "down" : Number(item?.score) >= 4 ? "up" : "equal"]
                return (
                  <div
                    role={allowDrilldown ? "button" : undefined}
                    onClick={() => allowDrilldown && onSelect?.(item?.userId)}
                    className={`flex items-center justify-between gap-4 rounded-md p-2 ${allowDrilldown ? "hover:bg-[#F5F5F5] cursor-pointer" : "cursor-default"} ${selectedUserID === item?.userId ? "bg-[#F5F5F5]" : ""}`}
                  >
                    <div className="flex items-center">
                      <div className="flex items-center w-[40px] pr-1 text-text2 text-[15px]">
                        {index + 1}
                        <div className="text-xs mb-1">
                          {index + 1 === 1 ? "st" : index + 1 === 2 ? "nd" : "rd"}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div>
                          {item?.profileImg ? (
                            <ImageView
                              className="w-8 h-8 rounded-full object-cover"
                              src={item?.profileImg}
                              alt="user"
                            />
                          ) : (
                            <div className="bg-primary w-8 h-8 rounded-full text-text-secondary text-sm flex justify-center items-center font-semibold">
                              {getInitial(
                                item?.name?.split(" ")[0] || "",
                                item?.name?.split(" ")[1] || ""
                              )}
                            </div>
                          )}
                        </div>
                        <span className="text-text2 text-[15px]">
                          {item?.name}
                        </span>
                      </div>
                    </div>
                    <div>
                      {mode === "sessions" ? (
                        <div className="w-16 h-[37px] flex items-center justify-center text-[13px] text-text2">
                          {showLabel ? item.label : item?.session_count ?? 0}
                        </div>
                      ) : (
                        <div className={`rounded-md gap-2 w-16 h-[37px] flex items-center justify-center text-[13px] ${statusData.bg} ${statusData.text}`}>
                          {showLabel ? item.label : Math.round((item?.score ?? 0) * 100) / 100}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
          </>
        </div>
      )}
    </Card>
  );
}
